import { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getAxiosOrErrorMessage, getErrorCode } from '../../utils/error.utils';
import {
  DEFAULT_USAGE_RECORD_QUANTITY,
  USAGE_ACTION,
  USAGE_ACTION_VALUES,
} from '../../constants/sample.constants';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import BaseDateTimeField from './BaseDateTimeField';
import { AppModalCloseButton } from '../common/AppModalCloseButton';

const usageRecordSchema = (t: (key: string) => string) =>
  z.object({
    quantity: z.number().int().min(0, t('usage_record.quantity_min_error')),
    usageDate: z.string().min(1, t('usage_record.usage_date_required')),
    purpose: z.string().optional(),
    notes: z.string().optional(),
    action: z.enum([...USAGE_ACTION_VALUES] as [string, ...string[]]).optional(),
  });

export type UsageRecordFormData = z.infer<ReturnType<typeof usageRecordSchema>>;

interface UsageRecordFormModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (data: UsageRecordFormData) => Promise<void>;
  sampleId: number;
  remainingQuantity?: number;
  /** 残量表示用（空なら既定「本」） */
  unit?: string | null;
  isLoading?: boolean;
}

export default function UsageRecordFormModal({
  show,
  onHide,
  onSubmit,
  sampleId: _sampleId,
  remainingQuantity,
  unit,
  isLoading = false,
}: UsageRecordFormModalProps) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string>('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UsageRecordFormData>({
    resolver: zodResolver(usageRecordSchema(t)),
    defaultValues: {
      quantity: DEFAULT_USAGE_RECORD_QUANTITY,
      usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      purpose: '',
      notes: '',
      action: USAGE_ACTION.USE,
    },
  });

  const quantity = watch('quantity');
  const usageDate = watch('usageDate');

  useEffect(() => {
    if (show) {
      reset({
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        usageDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        purpose: '',
        notes: '',
        action: USAGE_ACTION.USE,
      });
      setError('');
    }
  }, [show, reset]);

  const onSubmitForm = async (data: UsageRecordFormData) => {
    try {
      setError('');

      if (remainingQuantity !== undefined && data.quantity > remainingQuantity) {
        setError(
          t('usage_record.quantity_exceeds_remaining', {
            remaining: formatSampleQuantityWithUnit(remainingQuantity, unit, t),
          })
        );
        return;
      }

      await onSubmit(data);
      onHide();
    } catch (err: unknown) {
      const code = getErrorCode(err);
      const msg = getAxiosOrErrorMessage(err) || '';
      if (
        code === 'ECONNABORTED' ||
        msg.includes('timeout') ||
        msg.includes('api.error.request_timeout')
      ) {
        setError(t('api.error.request_timeout'));
      } else if (
        msg.startsWith('api.error.') ||
        msg.startsWith('sample.') ||
        msg.startsWith('usage_record.') ||
        msg.startsWith('common.')
      ) {
        setError(t(msg, { defaultValue: msg }));
      } else if (msg) {
        setError(msg);
      } else {
        setError(t('common.save_failed'));
      }
    }
  };

  return (
    <Modal
      dialogClassName="sample-system-dialog"
      show={show}
      onHide={onHide}
      /* ネイティブ datetime ピッカーが Modal 外に出るためフォーカス閉じ込めを外す */
      enforceFocus={false}
    >
      <Modal.Header>
        <Modal.Title className="sample-detail-modal-title pe-2">
          <i className="fas fa-flask me-2 text-primary" aria-hidden="true" />
          {t('usage_record.modal_title')}
        </Modal.Title>
        <AppModalCloseButton
          onClick={onHide}
          ariaLabel={t('common.close')}
          title={t('usage_record.modal_close_title', {
            defaultValue: '使用記録の追加を閉じる',
          })}
        />
      </Modal.Header>
      <Form id="usage-record-form" onSubmit={handleSubmit(onSubmitForm)}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="usage-quantity" title={t('usage_record.quantity')}>
                  {t('usage_record.field_quantity_label')} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  id="usage-quantity"
                  name="quantity"
                  type="number"
                  min="0"
                  max={remainingQuantity}
                  aria-label={t('usage_record.quantity')}
                  title={t('usage_record.field_quantity_title', {
                    defaultValue: '使用する数量を入力',
                  })}
                  aria-required="true"
                  aria-describedby={
                    remainingQuantity !== undefined ? 'usage-quantity-remaining' : undefined
                  }
                  {...register('quantity', { valueAsNumber: true })}
                  isInvalid={!!errors.quantity}
                  autoComplete="off"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.quantity?.message}
                </Form.Control.Feedback>
                {remainingQuantity !== undefined ? (
                  <Form.Text id="usage-quantity-remaining" className="text-muted d-block">
                    {joinLabeledValue(
                      t('usage_record.remaining_quantity_label'),
                      formatSampleQuantityWithUnit(remainingQuantity, unit, t)
                    )}
                  </Form.Text>
                ) : null}
                {remainingQuantity !== undefined && quantity > remainingQuantity ? (
                  <Form.Text className="text-danger d-block">
                    {t('usage_record.quantity_cannot_exceed_remaining')}
                  </Form.Text>
                ) : null}
                {quantity === 0 ? (
                  <Form.Text className="text-muted d-block" title={t('usage_record.quantity_note')}>
                    {t('usage_record.quantity_note')}
                  </Form.Text>
                ) : null}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label
                  htmlFor="usage-action"
                  title={t('usage_record.action_title', {
                    defaultValue: '使用 · 廃棄などの操作を選ぶ',
                  })}
                >
                  {t('usage_record.action')}
                </Form.Label>
                <Form.Select
                  id="usage-action"
                  aria-label={t('usage_record.action')}
                  title={t('usage_record.action_title', {
                    defaultValue: '使用 · 廃棄などの操作を選ぶ',
                  })}
                  {...register('action')}
                  isInvalid={!!errors.action}
                >
                  <option value={USAGE_ACTION.USE}>{t('usage_record.action_use')}</option>
                  <option value={USAGE_ACTION.DISPOSE}>{t('usage_record.action_dispose')}</option>
                  <option value={USAGE_ACTION.MOVE}>{t('usage_record.action_move')}</option>
                  <option value={USAGE_ACTION.OTHER}>{t('usage_record.action_other')}</option>
                </Form.Select>
                {errors.action && (
                  <Form.Control.Feedback type="invalid">
                    {errors.action.message}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <BaseDateTimeField
              id="usage-date"
              name="usageDate"
              label={t('usage_record.field_usage_date_label')}
              title={t('usage_record.field_usage_date_title', {
                defaultValue: '使用日時を選択',
              })}
              required
              value={usageDate}
              onChange={(v) =>
                setValue('usageDate', v, { shouldValidate: true, shouldDirty: true })
              }
              isInvalid={!!errors.usageDate}
              lang={i18n.language}
            />
            {errors.usageDate ? (
              <div className="invalid-feedback d-block">{errors.usageDate.message}</div>
            ) : null}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="usage-purpose">{t('usage_record.field_purpose_label')}</Form.Label>
            <Form.Control
              id="usage-purpose"
              name="purpose"
              type="text"
              aria-label={t('usage_record.field_purpose_label')}
              title={t('usage_record.purpose_placeholder')}
              {...register('purpose')}
              placeholder={t('usage_record.purpose_placeholder')}
              autoComplete="off"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="usage-notes">{t('usage_record.field_notes_label')}</Form.Label>
            <Form.Control
              id="usage-notes"
              name="notes"
              as="textarea"
              rows={3}
              aria-label={t('usage_record.field_notes_label')}
              title={t('usage_record.notes_placeholder')}
              {...register('notes')}
              placeholder={t('usage_record.notes_placeholder')}
              autoComplete="off"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={isLoading}
            aria-label={t('common.cancel')}
            title={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="usage-record-form"
            disabled={isLoading}
            aria-label={isLoading ? t('common.saving') : t('usage_record.add')}
            title={
              isLoading
                ? t('common.saving')
                : t('usage_record.submit_add_title', {
                    defaultValue: '使用記録を登録する',
                  })
            }
          >
            {isLoading ? t('common.saving') : t('usage_record.add')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
